import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { MessageService } from 'primeng/api';
import { RecordingSessionService } from '../recording/recording-session.service';
import { prefersCompactShell } from '../utils/layout.util';

import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { StripeService } from '../services/stripe.service';
import { HelpComponent } from '../help/help.component';

interface ShellNavItem {
  label: string;
  route: string;
  icon: 'visits' | 'patients' | 'analytics' | 'analyzer' | 'settings' | 'subscription';
}

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, ToastModule, DialogModule, ButtonModule, HelpComponent],
  templateUrl: './app-shell.component.html',
  styleUrl: './app-shell.component.scss',
})
export class AppShellComponent {
  private readonly router = inject(Router);
  private readonly recordingSession = inject(RecordingSessionService);
  private readonly stripeService = inject(StripeService);

  readonly navItems: ShellNavItem[] = [
    { label: 'Visits', route: '/', icon: 'visits' },
    { label: 'Patients', route: '/patients', icon: 'patients' },
    { label: 'Analytics', route: '/analytics', icon: 'analytics' },
    { label: 'Analyzer', route: '/analyzer', icon: 'analyzer' },
    { label: 'Subscription', route: '/subscription', icon: 'subscription' },
  ];

  activeRoute = '/';
  private newVisitQuery = false;
  hasSubscription = true;

  private readonly messageService = inject(MessageService);

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('subscription-required-error', () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Subscription Required',
          detail: 'You need an active subscription to perform this action.',
          life: 5000
        });
      });
    }

    let sessionId = undefined;
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      sessionId = urlParams.get('session_id') || undefined;
    }

    this.stripeService.getSubscriptionStatus(sessionId).subscribe({
      next: (res) => {
        const isActive = res.status === 'active';
        const hasAccess = !!res.hasAccessWithoutSubscription;
        this.hasSubscription = isActive || hasAccess;
      },
      error: () => {
        this.hasSubscription = false;
      }
    });

    const sync = () => {
      const url = this.router.url;
      const raw = url.split('?')[0].split('#')[0] || '/';
      this.activeRoute = raw.length > 1 && raw.endsWith('/') ? raw.slice(0, -1) : raw;
      this.newVisitQuery = this.router.parseUrl(url).queryParams['new'] != null;
    };
    sync();
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => sync());
  }

  isActive(route: string): boolean {
    if (this.isNewVisitActive() && route === '/') {
      return false;
    }
    if (route === '/') {
      return this.activeRoute === '/' || this.activeRoute === '/dashboard';
    }
    return this.activeRoute === route || this.activeRoute.startsWith(`${route}/`);
  }

  isNewVisitActive(): boolean {
    return this.activeRoute === '/recording' || this.newVisitQuery;
  }

  onNavClick(item: ShellNavItem, event: Event): void {
    if (item.route !== '/' || !prefersCompactShell()) {
      return;
    }
    event.preventDefault();
    void this.router.navigate(['/'], { queryParams: { list: '1' } });
  }

  onNewVisit(): void {
    if (this.recordingSession.isCaptureActive()) {
      this.recordingSession.requestNewRecordingFromNav();
      return;
    }
    void this.router.navigate(['/recording'], { queryParams: { autoStart: '1' } });
  }

  onLogout(): void {
    void this.router.navigate(['/logout']);
  }

  goToSubscription(): void {
    void this.router.navigate(['/subscription']);
  }
}
